import { useEffect, useState } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import moment from 'moment';
import { ProjectSettings } from 'shared/types/projectSettings/ProjectSettings';
import { SoldAccount } from 'shared/types/soa/customer/SoaCustomer';
import { PAYLOAD_QUERY } from '@/components/customercenter-ui/components/customer-detail/constants';
import Caution from '@/components/customercenter-ui/icons/caution';
import StoneBerryCredit from '@/components/customercenter-ui/icons/stoneBerryCreditLogo';
import BirchlandMarketCredit from '@/components/customercenter-ui/svgs/birchlandMarketLogo';
import FifthAndGlamCredit from '@/components/customercenter-ui/svgs/fifthAndGlamCredit';
import FBCredit from '@/components/customercenter-ui/svgs/figisGalleryLogoFB';
import FGCredit from '@/components/customercenter-ui/svgs/figisGiftsLogoFG';
import KJordanCredit from '@/components/customercenter-ui/svgs/kJordanlogo';
import MasonEasyPayCredit from '@/components/customercenter-ui/svgs/masoneasypay-credit';
import MasseysCredit from '@/components/customercenter-ui/svgs/masseys-credit';
import { useCustomObjects } from '@/frontastic';
import { useProjSettings } from '@/frontastic/hooks/useProjectSettings/useProjectSettings';
import { mapLocaleToMeaningfulFormat } from '@/helpers/utils/i18n';
import ContentStackImage from '../../../common/contentStackImage/contentStackImage';
import { maskPhoneNumber, maskPostalCode } from '../../utils/formatUtils';

interface SoldAccountProps {
  isSoldAccount: boolean;
  creditAccountNumber: string;
  debtBuyerCode: string;
  accountSoldOn: string;
}

const SoldAccountBanner: React.FC<SoldAccountProps> = ({
  isSoldAccount,
  creditAccountNumber,
  debtBuyerCode,
  accountSoldOn,
}) => {
  const projectSettings: ProjectSettings = useProjSettings();
  const { getDebtBuyerData } = useCustomObjects();
  const [soldAccountData, setSoldAccountData] = useState<SoldAccount>();
  const isAssetSaleEnabled = projectSettings?.assetSaleEnabled;
  const params = useSearchParams();
  const divisionCode = params.get(PAYLOAD_QUERY.DIVISION_CODE) ?? 'ZB';

  const getCreditIcon = () => {
    switch (divisionCode) {
      case 'ZD':
        return <MasonEasyPayCredit className="h-[29px] w-[275px]" />;
      case 'ZB':
        return <StoneBerryCredit />;
      case 'ZY':
        return <MasseysCredit />;
      case 'ZC':
        return <FifthAndGlamCredit />;
      case 'ZA':
        return <KJordanCredit />;
      case 'ZF':
        return <BirchlandMarketCredit />;
      case 'FB':
        return <FBCredit />;
      case 'FG':
        return <FGCredit />;
    }
  };

  const creditAccountName = () => {
    switch (divisionCode) {
      case 'ZD':
        return 'Mason Easy-Pay';
      case 'ZB':
        return 'Stoneberry';
      case 'ZY':
        return 'Masseys';
      case 'ZC':
        return 'Fifth & Glam';
      case 'ZA':
        return 'K Jordan';
      case 'ZF':
        return 'Birchland Market';
      case 'FB':
        return "Figi's Gallery";
      case 'FG':
        return "Figi's GIGT";
    }
  };

  const fetchDebtBuyersDataStorage = (debtBuyerCode: string) => {
    let debtBuyersDataFromStorage;
    const debtBuyersDataLocalStorage = localStorage.getItem('debtBuyerData');
    if (debtBuyersDataLocalStorage) {
      try {
        const debtBuyersData = JSON.parse(debtBuyersDataLocalStorage);
        if (debtBuyersData) {
          const { data, isSucess, date } = debtBuyersData;
          if (isSucess && date && data) {
            const dateObj = moment(date);
            const currentDate = new Date();
            const currentDateObj = moment(currentDate?.getTime());
            const hoursDiff = currentDateObj.diff(dateObj, 'hours');
            if (data[debtBuyerCode] && hoursDiff <= 6) {
              debtBuyersDataFromStorage = data[debtBuyerCode];
            }
          }
        }
      } catch (error) {
        console.error('Error while parsing debt buyers data');
      }
    }
    return debtBuyersDataFromStorage;
  };

  const fetchDebtBuyerData = async (debtBuyerCode: string) => {
    try {
      const debtBuyersDataFromStorage = fetchDebtBuyersDataStorage(debtBuyerCode);
      if (debtBuyersDataFromStorage) {
        setSoldAccountData(debtBuyersDataFromStorage);
      } else {
        const debtBuyers = await getDebtBuyerData();
        if (debtBuyers && Object.entries(debtBuyers)?.length > 0) {
          if (debtBuyers[debtBuyerCode]) {
            setSoldAccountData(debtBuyers[debtBuyerCode]);
            const debtBuyersData = {
              data: debtBuyers,
              isSucess: true,
              date: new Date(),
            };
            localStorage.setItem('debtBuyerData', JSON.stringify(debtBuyersData));
          }
        }
      }
    } catch (error) {
      console.error('Error while fetching debt buyers data.');
    }
  };

  useEffect(() => {
    if (isAssetSaleEnabled && isSoldAccount && debtBuyerCode) {
      fetchDebtBuyerData(debtBuyerCode);
    }
  }, [isAssetSaleEnabled, debtBuyerCode, isSoldAccount]);

  return (
    <section>
      {isAssetSaleEnabled && soldAccountData && (
        <section className="grid grid-cols-1 rounded-md border-2 border-[#D6A124] bg-white text-[#333]">
          <section className="flex justify-center gap-2 rounded-t-md bg-[#FFF3E380] px-5 py-4 lg:items-center lg:px-0">
            <Caution />
            <p className="text-[22px] font-bold text-[#D6A124]">{mapLocaleToMeaningfulFormat().accountSold}</p>
          </section>
          <section className="px-4 lg:px-0">
            <section className="my-[25px] flex flex-col items-center gap-2">
              {getCreditIcon()}
              <p className="font-bold">
                {mapLocaleToMeaningfulFormat().customerAccount}{' '}
                <span className="gbmask font-bold">{creditAccountNumber}</span>
              </p>
            </section>
            <section className="flex flex-col items-center text-center lg:mx-[80px]">
              <p
                className="font-bold"
                dangerouslySetInnerHTML={{
                  __html:
                    divisionCode === 'ZD'
                      ? mapLocaleToMeaningfulFormat([
                          `${creditAccountName()}`,
                          `${soldAccountData?.name}`,
                          `${accountSoldOn}`,
                        ]).accountSoldMsgZD
                      : mapLocaleToMeaningfulFormat([
                          `${creditAccountName()}`,
                          `${soldAccountData?.name}`,
                          `${accountSoldOn}`,
                        ]).accountSoldMsg,
                }}
              />
              <p className="mt-5">{mapLocaleToMeaningfulFormat([`${soldAccountData?.name}`]).accountSolContactMsg}</p>
            </section>
            <section className="flex flex-col items-center space-y-2 pt-5">
              {soldAccountData?.imageURL && (
                <ContentStackImage src={`${soldAccountData?.imageURL}`} imageWidth={197} imageHeight={94} />
              )}
              <Link
                href={`${soldAccountData?.websiteURL}`}
                target="_blank"
                rel="noopener noreferrer"
                className="block text-[#0075C9] underline"
              >
                {soldAccountData?.websiteURL}
              </Link>
              <p className="font-bold">{soldAccountData?.name}</p>
            </section>
            <section className="mb-5 flex flex-col items-center">
              <p>{soldAccountData?.addrLine1}</p>
              {soldAccountData?.addrLine2 && <p>{soldAccountData?.addrLine2}</p>}
              <p>
                {soldAccountData?.city}, {soldAccountData?.state} {maskPostalCode(soldAccountData?.zip ?? '')}
                {soldAccountData?.zip4 && `-${soldAccountData.zip4}`}
              </p>
              <p>{maskPhoneNumber(soldAccountData?.phoneNumber ?? '')}</p>
              <Link href={`mailto:${soldAccountData?.emailAddr}`} className="text-[#0075C9] underline">
                {soldAccountData?.emailAddr}
              </Link>
            </section>
          </section>
        </section>
      )}
    </section>
  );
};

export default SoldAccountBanner;
